import { useOrganisation } from '@/app/redux/hooks/organisation';
import { ProIcon } from '@/common/ui/UpgradeButton';
import { getRecommendedPlan } from '@/common/utils/license';
import { CheckIcon, MinusIcon } from '@phosphor-icons/react';
import { Badge } from '@shadcn/components/badge';
import { Button } from '@shadcn/components/button';
import { cn } from '@shadcn/lib/utils';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

interface FeatureTableProps {
  showRecommended?: boolean;
  showPricing?: boolean;
  showCTAs?: boolean;
}
export default function FeatureTable({
  showRecommended,
  showPricing,
  showCTAs,
}: FeatureTableProps) {
  const navigate = useNavigate();

  const { organisation } = useOrganisation();

  const recommended = useMemo(
    () => getRecommendedPlan(organisation?.studentCount ?? 0),
    [organisation?.studentCount],
  );

  const recommendFamily = showRecommended && recommended === 'family';
  const recommendPro = !showRecommended || recommended !== 'family';

  return (
    <table className="border-collapse w-full">
      <thead>
        <tr className="border-b-2 border-border text-xl">
          <th className="w-2/5" />
          <th className="w-1/5 font-semibold text-center pt-2 pb-1">Free</th>
          <th
            className={cn(
              'w-1/5 font-semibold text-center pt-2 pb-1 rounded-t-lg relative',
              recommendFamily && 'bg-accent',
            )}
          >
            Family
            {recommendFamily && (
              <Badge className="absolute -top-4 -right-2">
                <CheckIcon size={16} className="mr-2" />
                <span>Best Value</span>
              </Badge>
            )}
          </th>
          <th
            className={cn(
              'w-1/5 font-semibold text-center pt-2 pb-1 rounded-t-lg relative',
              recommendPro && 'bg-accent',
            )}
          >
            <div className="flex items-center justify-center gap-1">
              <span>Pro</span>
              <ProIcon />
            </div>
            {showRecommended && recommendPro && (
              <Badge className="absolute -top-4 -right-2">
                <CheckIcon size={16} className="mr-2" />
                <span>Best Value</span>
              </Badge>
            )}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr className="border-b border-border/50">
          <td className="text-end py-2 pr-2">
            <div className="flex flex-col">
              <span className="font-semibold leading-tight">
                Lesson Planner & Word Mats
              </span>
              <span className="text-xs text-muted-foreground mt-1 leading-relaxed">
                Easy to use, customisable lesson plans to support instruction.
              </span>
            </div>
          </td>
          <td className="px-2">
            <div className="grid place-items-center">
              <CheckIcon className="size-6 text-primary" weight="bold" />
            </div>
          </td>
          <td className={cn('px-2', recommendFamily && 'bg-accent')}>
            <div className="grid place-items-center">
              <CheckIcon className="size-6 text-primary" weight="bold" />
            </div>
          </td>
          <td className={cn('px-2', recommendPro && 'bg-accent')}>
            <div className="grid place-items-center">
              <CheckIcon className="size-6 text-primary" weight="bold" />
            </div>
          </td>
        </tr>
        <tr className="border-b border-border/50">
          <td className="text-end py-2 pr-2">
            <div className="flex flex-col">
              <span className="font-semibold leading-tight">Assessments</span>
              <span className="text-xs text-muted-foreground mt-1 leading-relaxed">
                Assign in-game assessments to guage learner ability.
              </span>
            </div>
          </td>
          <td className="px-2">
            <div className="grid place-items-center">
              <MinusIcon className="size-6 text-primary" weight="bold" />
            </div>
          </td>
          <td className={cn('px-2', recommendFamily && 'bg-accent')}>
            <div className="grid place-items-center">
              <CheckIcon className="size-6 text-primary" weight="bold" />
            </div>
          </td>
          <td className={cn('px-2', recommendPro && 'bg-accent')}>
            <div className="grid place-items-center">
              <CheckIcon className="size-6 text-primary" weight="bold" />
            </div>
          </td>
        </tr>
        <tr className="border-b border-border/50">
          <td className="text-end py-2 pr-2">
            <div className="flex flex-col">
              <span className="font-semibold leading-tight">
                Rich Data & Insights
              </span>
              <span className="text-xs text-muted-foreground mt-1 leading-relaxed">
                To track progress and identify learning gaps.
              </span>
            </div>
          </td>
          <td className="px-2">
            <div className="grid place-items-center">
              <MinusIcon className="size-6 text-primary" weight="bold" />
            </div>
          </td>
          <td className={cn('px-2', recommendFamily && 'bg-accent')}>
            <div className="grid place-items-center text-center text-xs">
              Activity Feed only
            </div>
          </td>
          <td className={cn('px-2', recommendPro && 'bg-accent')}>
            <div className="grid place-items-center">
              <CheckIcon className="size-6 text-primary" weight="bold" />
            </div>
          </td>
        </tr>
        <tr
          className={cn(
            (showPricing || showCTAs) && 'border-b border-border/50',
          )}
        >
          <td className="text-end py-2 pr-2">
            <div className="flex flex-col">
              <span className="font-semibold leading-tight">
                Spellcaster Game
              </span>
              <span className="text-xs text-muted-foreground mt-1 leading-relaxed">
                A spelling-driven adventure game for fun, independent practice.
              </span>
            </div>
          </td>
          <td className="px-2">
            <div className="grid place-items-center">
              <MinusIcon className="size-6 text-primary" weight="bold" />
            </div>
          </td>
          <td
            className={cn(
              'px-2',
              recommendFamily && 'bg-accent',
              !showPricing && !showCTAs && 'rounded-b-lg',
            )}
          >
            <div className="grid place-items-center text-center text-xs">
              3 learner accounts
            </div>
          </td>
          <td
            className={cn(
              'px-2',
              recommendPro && 'bg-accent',
              !showPricing && !showCTAs && 'rounded-b-lg',
            )}
          >
            <div className="grid place-items-center text-center text-xs">
              20+ learner accounts
            </div>
          </td>
        </tr>
        {showPricing && (
          <tr>
            <td className="px-2 py-3 text-end">
              <span className="font-semibold leading-tight">Pricing</span>
            </td>
            <td className="px-2 py-3">
              <div className="grid place-items-center text-center">
                <div className="flex flex-col">
                  <span className="text-2xl font-bold text-foreground">
                    Free
                  </span>
                  <span className="text-xs text-muted-foreground mt-0.5">
                    Forever
                  </span>
                </div>
              </div>
            </td>
            <td className={cn('px-2 py-3', recommendFamily && 'bg-accent')}>
              <div className="grid place-items-center text-center">
                <div className="flex flex-col">
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-2xl font-bold">$20</span>
                    <span className="text-sm text-muted-foreground">/mo</span>
                  </div>
                  <span className="text-xs text-muted-foreground mt-0.5">
                    or $99/yr (save 17%)
                  </span>
                </div>
              </div>
            </td>
            <td className={cn('px-2 py-3', recommendPro && 'bg-accent')}>
              <div className="grid place-items-center text-center">
                <div className="flex flex-col">
                  <span className="text-sm text-muted-foreground">
                    Plans from
                  </span>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-2xl font-bold">$200</span>
                    <span className="text-sm text-muted-foreground">/yr</span>
                  </div>
                </div>
              </div>
            </td>
          </tr>
        )}
        {showCTAs && (
          <tr>
            <td />
            <td className="px-2 py-3">
              <div className="grid place-items-center text-center text-sm text-muted-foreground">
                Current plan
              </div>
            </td>
            <td
              className={cn(
                'px-2 py-3 rounded-b-lg',
                recommendFamily && 'bg-accent',
              )}
            >
              <div className="grid place-items-center text-center">
                <Button onClick={() => navigate('/pricing')}>
                  Select plan
                </Button>
              </div>
            </td>
            <td
              className={cn(
                'px-2 py-3 rounded-b-lg',
                recommendPro && 'bg-accent',
              )}
            >
              <div className="grid place-items-center text-center">
                <Button onClick={() => navigate('/pricing')}>
                  View options
                </Button>
              </div>
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}
